<template>
    <AppLayout>
        <div class="max-w-7xl mx-auto px-4 py-6">
            <!-- Page Title -->
            <div class="mb-6">
                <h1 class="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                    {{ $tc('LBL_ORDER_REPORT', 2) }}
                </h1>
                <!-- Breadcrumb -->
                <nav
                    class="text-sm text-gray-500 mb-6"
                    aria-label="Breadcrumb"
                >
                    <ol class="flex items-center space-x-2">
                        <li class="flex items-center">
                            <inertia-link
                                :href="route('landing')"
                                class="text-green-600 hover:underline"
                            >
                                {{ $t('LBL_HOME') }}
                            </inertia-link>
                            <svg
                                class="w-3 h-3 mx-2 text-green-400"
                                fill="currentColor"
                                viewBox="0 0 320 512"
                            >
                                <path
                                    d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"
                                />
                            </svg>
                        </li>
                        <li class="text-gray-700">
                            {{ $tc('LBL_ORDER', 1) }}
                        </li>
                    </ol>
                </nav>
            </div>
            <div class="w-full my-3 text-xs bg-white rounded shadow-sm md:text-sm">
                <div class="flex flex-col md:items-center md:flex-row md:justify-between">
                    <div class="flex flex-col p-2">
                        <p class="font-semibold">
                            Order <span class="text-green-600">#{{ bill.bill_number }} </span>
                        </p>

                        <div
                            v-if="bill.wholesaler || bill.user"
                            class="flex"
                        >
                            <span class="mr-1 text-gray-400">
                                {{ $page.props.roles[0] === 'retailer' ? 'From' : 'By' }}</span
                            >
                            <p class="font-semibold">
                                {{ $page.props.roles[0] === 'retailer' ? bill.wholesaler.name : bill.user.name }}
                            </p>
                        </div>

                        <p class="text-xs text-gray-400">Placed on {{ bill.created_at | readableDate }}</p>
                    </div>

                    <!-- Print and Download PDF -->
                    <div class="flex items-center gap-4 p-2">
                        <div class="flex gap-4">
                            <t-button
                                @click="printInvoice(bill)"
                                variant="success"
                                class="flex flex-row gap-2"
                            >
                                Print
                            </t-button>
                            <!-- <t-button
                                @click="exportToPDF()"
                                variant="outline"
                                class="px-4 py-2"
                            >
                                Download PDF
                            </t-button> -->
                        </div>
                    </div>
                </div>

                <!-- Order Details Wrapper -->
                <div class="flex flex-col md:flex-row gap-6 p-4">
                    <!-- Product List -->
                    <div class="flex-1">
                        <div
                            v-for="order in bill.transactions"
                            :key="order.id"
                            class="flex flex-col md:flex-row gap-4 p-4 mb-4 bg-white border rounded shadow-sm"
                        >
                            <img
                                :src="
                                    order.product.images.length ? order.product.images[0].image_path : common.defaultImg
                                "
                                class="w-full md:w-32 h-32 object-cover rounded cursor-pointer"
                                @click="gotoProduct(order)"
                            />
                            <div class="flex-grow">
                                <h3
                                    class="text-base font-semibold text-gray-800 cursor-pointer line-clamp-2"
                                    @click="gotoProduct(order)"
                                >
                                    {{ order.product.name }}
                                </h3>
                                <p class="text-sm text-gray-500 mb-2">{{ order.product.brand.name }}</p>
                            </div>
                            <div>
                                <div class="text-sm text-gray-600">
                                    <p>Qty: {{ order.qty }}</p>
                                    <p>
                                        Price: {{ config(common.currency, order.product.owner) }}
                                        {{ parseFloat(order.product.price).toFixed(2) }}
                                    </p>
                                    <p v-if="order.vat != 0">
                                        VAT: {{ config(common.currency, order.product.owner) }} {{ order.vat }}
                                    </p>
                                </div>
                                <div class="mt-2 text-green-600 font-semibold">
                                    Total: {{ config(common.currency, order.product.owner) }}
                                    {{ parseFloat(order.grand_total).toFixed(2) }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Sticky Summary Section -->
                    <div class="md:w-1/3 h-fit sticky top-4">
                        <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                            <h2 class="text-lg font-semibold mb-4 text-gray-800">Order Summary</h2>
                            <div class="text-sm space-y-2">
                                <div class="flex justify-between">
                                    <span class="text-gray-600">Payment Method</span>
                                    <span class="font-semibold">{{ bill.payment_method }}</span>
                                </div>
                                <div class="flex justify-between">
                                    <span class="text-gray-600">Subtotal</span>
                                    <span class="font-semibold">
                                        {{ config(common.currency) }} {{ subtotalWithVat }}
                                    </span>
                                </div>
                                <div class="flex justify-between">
                                    <span class="text-gray-600">Total Discount</span>
                                    <span class="text-red-500 font-semibold"
                                        >- {{ config(common.currency) }} {{ totalDiscountAmount }}</span
                                    >
                                </div>
                                <hr />
                                <div class="flex justify-between text-lg font-semibold text-green-600">
                                    <span>Grand Total</span>
                                    <span
                                        >{{ config(common.currency) }} {{ subtotalWithVat - totalDiscountAmount }}</span
                                    >
                                </div>
                            </div>

                            <!-- Notes -->
                            <div class="mt-6">
                                <h3 class="font-semibold text-sm text-gray-700 mb-1">Notes</h3>
                                <p class="text-gray-600 text-sm">{{ bill.notes || '--' }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </AppLayout>
</template>

<script>
    import AppLayout from '@/Layouts/AppLayout.vue';
    import common from '@/common';

    export default {
        name: 'TransactionsOfBill',

        components: {
            AppLayout,
        },

        props: {
            bill: {
                type: Object,
                required: true,
            },
        },

        data() {
            return {
                common,
            };
        },

        computed: {
            subtotalWithVat() {
                return this.bill.transactions
                    .reduce((acc, transaction) => {
                        const totalWithVat = parseFloat(transaction.total) + parseFloat(transaction.vat);
                        return acc + totalWithVat;
                    }, 0)
                    .toFixed(2);
            },

            totalDiscountAmount() {
                return this.bill.transactions
                    .reduce((acc, transaction) => acc + parseFloat(transaction.discount), 0)
                    .toFixed(2);
            },
        },

        methods: {
            printInvoice(bill) {
                this.newTab('invoice.print', { billId: bill.id });
            },
        },
    };
</script>
